import { useEffect } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import '@/styles/tiptap.css';
import { useFormik } from 'formik';
import { toast } from 'sonner';

import { getPages } from '@/api/pages/getPages';
import { useCreatePageMutation } from '@/api/pages/createPage';
import { useUpdatePageMutation } from '@/api/pages/updatePage';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MenuBar } from '@/components/pages/MenuBar';

export const Route = createFileRoute('/dashboard/pages/$id')({
  loader: async ({ params }) => {
    const { pages } = await getPages();
    const page = pages.find(p => p.id === params.id);
    return { page };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { page } = Route.useLoaderData();
  const navigate = useNavigate();
  const { id } = Route.useParams();

  const createMutation = useCreatePageMutation();
  const updateMutation = useUpdatePageMutation();

  const isNewPage = id === 'new';

  const form = useFormik({
    initialValues: {
      id: page?.id || '',
      page_type: page?.page_type || '',
      content_en: page?.content_en || '',
      content_fr: page?.content_fr || '',
    },
    onSubmit: async values => {
      try {
        if (isNewPage) {
          const result = await createMutation.mutateAsync({
            pageType: values.page_type,
            contentEn: values.content_en,
            contentFr: values.content_fr,
          });
          navigate({ to: '/dashboard/pages/$id', params: { id: result.page.id } });
          toast('Page created!');
        } else {
          await updateMutation.mutateAsync({
            id: values.id,
            pageType: values.page_type,
            contentEn: values.content_en,
            contentFr: values.content_fr,
          });
          toast('Success', {
            description: 'The page has been successfully updated.',
          });
        }
      } catch (error) {
        console.error('Failed to save page:', error);
      }
    },
  });

  const editorEn = useEditor({
    extensions: [StarterKit],
    content: form.values.content_en || '<p>Start writing in English...</p>',
    onUpdate: ({ editor }) => {
      form.setFieldValue('content_en', editor.getHTML());
    },
  });

  const editorFr = useEditor({
    extensions: [StarterKit],
    content: form.values.content_fr || '<p>Commencez à écrire en français...</p>',
    onUpdate: ({ editor }) => {
      form.setFieldValue('content_fr', editor.getHTML());
    },
  });

  useEffect(() => {
    if (editorEn && page?.content_en) {
      editorEn.commands.setContent(page.content_en);
    }
    if (editorFr && page?.content_fr) {
      editorFr.commands.setContent(page.content_fr);
    }
  }, [page, editorEn, editorFr]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="container mx-auto py-6">
      <form
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();

          form.handleSubmit();
        }}
      >
        <Card className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">{isNewPage ? 'Create New Page' : 'Edit Page'}</h1>

            <div className="flex gap-2">
              <Link to="/dashboard/pages">
                <Button variant="ghost">Back</Button>
              </Link>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Page'}
              </Button>
            </div>
          </div>

          <div className="mb-6">
            <Label htmlFor="page_type">Page Type</Label>
            <Input
              id="page_type"
              name="page_type"
              value={form.values.page_type}
              onChange={form.handleChange}
              placeholder="e.g., privacy-policy, terms-of-service"
              className="mt-2"
            />
          </div>

          <Tabs defaultValue="en" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="en">English</TabsTrigger>
              <TabsTrigger value="fr">French</TabsTrigger>
            </TabsList>

            <TabsContent value="en">
              <div className="border rounded-md p-4 min-h-[400px] prose max-w-none">
                <MenuBar editor={editorEn} />
                <EditorContent editor={editorEn} />
              </div>
            </TabsContent>

            <TabsContent value="fr">
              <div className="border rounded-md p-4 min-h-[400px] prose max-w-none">
                <MenuBar editor={editorFr} />
                <EditorContent editor={editorFr} />
              </div>
            </TabsContent>
          </Tabs>
        </Card>
      </form>
    </div>
  );
}
